import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { toast } from '../../Components/Toast';
import { useAuth } from '../../Context/AuthContext';
import {
  getMySpace,
  getSharedWithMeSpaces,
  getSpaceOwnerLabel,
  readSharedSpaces,
  readSpaceProfile,
} from '../../services/spaceMembersService';

const ActiveSpaceContext = createContext(null);

const STORAGE_KEY = 'terrio_active_space_owner_user_id';

/**
 * Contexto de Espacio activo (invite v0).
 * `activeOwnerUserId === null` → propio espacio.
 * Header: `mySpaceHeaderLabel` = space_name o “Mi Espacio”.
 * `spaceSwitchSeq` sube solo en un cambio del usuario (enterSpace /
 * exitToOwnSpace), no al montar ni al restaurar el espacio de sessionStorage:
 * MapPage lo usa para cerrar paneles/detalles del espacio anterior.
 *
 * @see docs/maps/espacios-invite.md
 */
export function ActiveSpaceProvider({ children }) {
  const { isLoggedIn } = useAuth();
  const [sharedSpaces, setSharedSpaces] = useState([]);
  const [loadingSharedSpaces, setLoadingSharedSpaces] = useState(true);
  const [mySpaceProfile, setMySpaceProfile] = useState(null);
  const [activeOwnerUserId, setActiveOwnerUserId] = useState(null);
  const [spaceSwitchSeq, setSpaceSwitchSeq] = useState(0);

  const refreshSharedSpaces = useCallback(async (signal) => {
    if (!isLoggedIn) {
      setSharedSpaces([]);
      setLoadingSharedSpaces(false);
      return [];
    }
    setLoadingSharedSpaces(true);
    try {
      const res = await getSharedWithMeSpaces({ signal });
      if (signal?.aborted) return [];
      if (res._httpStatus === 401) {
        setSharedSpaces([]);
        return [];
      }
      const spaces = res._httpOk ? readSharedSpaces(res) : [];
      setSharedSpaces(spaces);
      return spaces;
    } catch (error) {
      if (error?.name === 'AbortError') return [];
      console.error('Espacios: GET /getsharedwithmespaces falló', error);
      setSharedSpaces([]);
      return [];
    } finally {
      if (!signal?.aborted) setLoadingSharedSpaces(false);
    }
  }, [isLoggedIn]);

  const refreshMySpace = useCallback(async (signal) => {
    if (!isLoggedIn) {
      setMySpaceProfile(null);
      return null;
    }
    try {
      const res = await getMySpace({ signal });
      if (signal?.aborted) return null;
      if (!res._httpOk) {
        setMySpaceProfile(null);
        return null;
      }
      const profile = readSpaceProfile(res);
      setMySpaceProfile(profile);
      return profile;
    } catch (error) {
      if (error?.name === 'AbortError') return null;
      console.error('Espacios: GET /getmyspace falló', error);
      setMySpaceProfile(null);
      return null;
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      setSharedSpaces([]);
      setMySpaceProfile(null);
      setActiveOwnerUserId(null);
      setLoadingSharedSpaces(false);
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
      return undefined;
    }

    const controller = new AbortController();
    (async () => {
      const [spaces] = await Promise.all([
        refreshSharedSpaces(controller.signal),
        refreshMySpace(controller.signal),
      ]);
      if (controller.signal.aborted) return;
      let stored = null;
      try {
        stored = sessionStorage.getItem(STORAGE_KEY);
      } catch {
        stored = null;
      }
      if (stored && spaces.some((s) => String(s.owner_user_id) === stored)) {
        setActiveOwnerUserId(stored);
      } else {
        setActiveOwnerUserId(null);
        try {
          sessionStorage.removeItem(STORAGE_KEY);
        } catch {
          // ignore
        }
      }
    })();

    return () => controller.abort();
  }, [isLoggedIn, refreshSharedSpaces, refreshMySpace]);

  const applyMySpaceProfile = useCallback((profile) => {
    if (!profile || typeof profile !== 'object') return;
    setMySpaceProfile((prev) => ({ ...(prev || {}), ...profile }));
  }, []);

  const activeSpace = useMemo(() => {
    if (!activeOwnerUserId) return null;
    return sharedSpaces.find((s) => String(s.owner_user_id) === activeOwnerUserId) || {
      owner_user_id: activeOwnerUserId,
    };
  }, [activeOwnerUserId, sharedSpaces]);

  const isViewingSharedSpace = Boolean(activeOwnerUserId);

  /** Header en espacio propio: space_name o “Mi Espacio”. */
  const mySpaceHeaderLabel = useMemo(() => {
    const named = String(mySpaceProfile?.space_name || '').trim();
    return named || 'Mi Espacio';
  }, [mySpaceProfile]);

  const activeSpaceLabel = activeSpace ? getSpaceOwnerLabel(activeSpace) : null;

  /** Ajeno: label del owner en el chip. Propio: el chip dice “Mi espacio”; este valor es el nombre del desplegable. */
  const headerSpaceLabel = isViewingSharedSpace
    ? (activeSpaceLabel || 'Espacio compartido')
    : mySpaceHeaderLabel;

  const enterSpace = useCallback((spaceOrOwnerId) => {
    const space = typeof spaceOrOwnerId === 'string' ? null : spaceOrOwnerId;
    const ownerId = typeof spaceOrOwnerId === 'string'
      ? spaceOrOwnerId.trim()
      : String(space?.owner_user_id || '').trim();
    if (!ownerId || ownerId === activeOwnerUserId) return;
    setActiveOwnerUserId(ownerId);
    setSpaceSwitchSeq((seq) => seq + 1);
    try {
      sessionStorage.setItem(STORAGE_KEY, ownerId);
    } catch {
      // ignore
    }
    const known = space || sharedSpaces.find((item) => String(item.owner_user_id) === ownerId);
    const label = known ? getSpaceOwnerLabel(known) : 'este espacio';
    toast.success(`Ahora estás en ${label}.`);
  }, [activeOwnerUserId, sharedSpaces]);

  const exitToOwnSpace = useCallback(() => {
    if (!activeOwnerUserId) return;
    setActiveOwnerUserId(null);
    setSpaceSwitchSeq((seq) => seq + 1);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    toast.success(`Volviste a ${mySpaceHeaderLabel}.`);
  }, [activeOwnerUserId, mySpaceHeaderLabel]);

  const value = useMemo(() => ({
    activeOwnerUserId,
    spaceSwitchSeq,
    activeSpace,
    activeSpaceLabel,
    headerSpaceLabel,
    mySpaceHeaderLabel,
    mySpaceProfile,
    isViewingSharedSpace,
    sharedSpaces,
    loadingSharedSpaces,
    enterSpace,
    exitToOwnSpace,
    refreshSharedSpaces,
    refreshMySpace,
    applyMySpaceProfile,
  }), [
    activeOwnerUserId,
    spaceSwitchSeq,
    activeSpace,
    activeSpaceLabel,
    headerSpaceLabel,
    mySpaceHeaderLabel,
    mySpaceProfile,
    isViewingSharedSpace,
    sharedSpaces,
    loadingSharedSpaces,
    enterSpace,
    exitToOwnSpace,
    refreshSharedSpaces,
    refreshMySpace,
    applyMySpaceProfile,
  ]);

  return (
    <ActiveSpaceContext.Provider value={value}>
      {children}
    </ActiveSpaceContext.Provider>
  );
}

/**
 * @returns {object}
 */
export function useActiveSpace() {
  const ctx = useContext(ActiveSpaceContext);
  if (!ctx) {
    throw new Error('useActiveSpace must be used within an ActiveSpaceProvider');
  }
  return ctx;
}
